import React, { FC, useEffect, useRef, useState } from 'react';
import { FilterPanel } from '../FilterPanel';
import { ProductFilter } from '../../pages/admin/videos/adminVideosV2/productFilter';
import { ProductCategoryFilter } from '../../pages/admin/videos/adminVideosV2/productCategoryFilter';
import { StoryFilter } from '../../pages/admin/videos/adminVideosV2/storyFilter';
import { ExpertFilter } from '../../pages/admin/videos/adminVideosV2/expertFilter';
import { ContentCodeFilter } from '../../pages/admin/videos/adminVideosV2/contentCodeFilter';
import { ExtraFilter } from '../../pages/admin/videos/adminVideosV2/extraFilter';
import { DemoFilter } from '../../pages/admin/videos/adminVideosV2/demoFilter';
import { PdpUrlFilter } from '../../pages/admin/videos/adminVideosV2/pdpUrlFilter';
import { InternalStatusFilter } from '../../pages/admin/videos/adminVideosV2/internalStatusFilter';
import { ApprovalFilter } from '../../pages/admin/videos/adminVideosV2/approvalFilter';
import { TagFilter } from '../../pages/admin/videos/adminVideosV2/tagFilter';
import { isInternal } from '../../utils';
import { useUser } from '../../hooks/useUser';

interface Props {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onFilterSelect: (key: string, id?: string) => void;
  onClearAll: () => void;
  activeCount: number;
  isExpertVideos?: boolean;
  isStoryVideos?: boolean;
  isProductVideos?: boolean;
}

/**
 * §04 — the panel behind "More filters". Filter fields fetch their own options
 * lazily on open (nothing preloads at section level, which is what removes the
 * old refetch-all-products-and-brands-on-every-change behavior).
 */
const FilterPopover: FC<Props> = ({
  anchorEl,
  onClose,
  onFilterSelect,
  onClearAll,
  activeCount,
  isExpertVideos,
  isStoryVideos,
  isProductVideos,
}) => {
  const { role } = useUser();
  const filterProps = { onFilterSelect } as any;

  // Bumped once per open (mount counts as the first open) so mutable option
  // lists (stories) can refetch per open while the rest stay cached.
  const [openSeq, setOpenSeq] = useState(1);
  const wasOpen = useRef(true);
  const isOpen = !!anchorEl;
  useEffect(() => {
    if (isOpen && !wasOpen.current) setOpenSeq((n: number) => n + 1);
    wasOpen.current = isOpen;
  }, [isOpen]);

  return (
    <FilterPanel anchorEl={anchorEl} onClose={onClose} onClearAll={onClearAll} activeCount={activeCount}>
      {!isProductVideos && <ProductFilter {...filterProps} creatable queryParams="videos=true" />}
      {!isProductVideos && <ProductCategoryFilter {...filterProps} />}
      {!isStoryVideos && <StoryFilter {...filterProps} creatable refreshKey={openSeq} />}
      {!isExpertVideos && <ExpertFilter {...filterProps} queryParams="videos=true" />}
      <ContentCodeFilter {...filterProps} />
      <ExtraFilter {...filterProps} />
      <PdpUrlFilter {...filterProps} />
      {isInternal(role) && <DemoFilter {...filterProps} />}
      {isInternal(role) && <InternalStatusFilter {...filterProps} />}
      {isInternal(role) && <ApprovalFilter {...filterProps} />}
      {isInternal(role) && <TagFilter {...filterProps} />}
    </FilterPanel>
  );
};

export default FilterPopover;
